import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface EventDocument {
    id: string;
    name: string;
    description?: string;
    docType: string;
    url?: string;
    createdAt: string;
    updatedAt: string;
}

interface EventFormData {
    name: string;
    description: string;
    file: File | null;
}

const EventsManagement: React.FC = () => {
    const { isAuthenticated, token, tenantId } = useAuth();
    const [events, setEvents] = useState<EventDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState<EventDocument | null>(null);
    const [formData, setFormData] = useState<EventFormData>({
        name: '',
        description: '',
        file: null
    });
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reindirizza alla login se non autenticato
    useEffect(() => {
        if (!isAuthenticated || !token || !tenantId) {
            window.location.href = '/login';
            return;
        }
    }, [isAuthenticated, token, tenantId]);

    // Carica tutti gli eventi
    const loadEvents = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiFetch('/api/tenant/self/documents');

            if (response.ok) {
                const text = await response.text();
                let documents = [];
                try {
                    documents = text ? JSON.parse(text) : [];
                } catch (parseError) {
                    console.error('Errore parsing JSON:', parseError, 'Response text:', text);
                    documents = [];
                }
                // Filtra solo i documenti di tipo EVENTO
                const eventDocs = documents.filter((doc: any) => doc.docType === 'EVENTO');
                setEvents(eventDocs);
            } else {
                const errorText = await response.text();
                console.error('Errore response:', response.status, errorText);
                setError('Errore nel caricamento degli eventi');
            }
        } catch (err) {
            console.error('Errore caricamento eventi:', err);
            setError('Errore di connessione');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEvents();
    }, []);

    // Reset form
    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            file: null
        });
        setEditingEvent(null);
        setShowForm(false);
        setError(null);
    };

    // Gestisce l'upload/update di un evento
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            setError('Il nome dell\'evento è obbligatorio');
            return;
        }

        if (!editingEvent && !formData.file) {
            setError('Seleziona un file PDF per l\'evento');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            let documentUrl = '';

            // Se c'è un file da caricare, prima fai l'upload
            if (formData.file) {
                const uploadFormData = new FormData();
                uploadFormData.append('file', formData.file);

                const uploadResponse = await apiFetch('/api/upload', {
                    method: 'POST',
                    body: uploadFormData,
                });

                if (!uploadResponse.ok) {
                    const errorText = await uploadResponse.text();
                    setError('Errore upload file: ' + errorText);
                    return;
                }

                const uploadResult = await uploadResponse.json();
                documentUrl = uploadResult.url;
            }

            // Prepara i dati del documento
            const documentData = {
                type: 'EVENTO',
                title: formData.name,
                description: formData.description,
                ...(documentUrl && { url: documentUrl })
            };

            const url = editingEvent
                ? `/api/tenant/self/documents/${editingEvent.id}`
                : '/api/tenant/self/documents';

            const method = editingEvent ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                body: JSON.stringify(documentData),
            }); if (response.ok) {
                await loadEvents();
                resetForm();
            } else {
                const errorData = await response.text();
                setError(errorData || 'Errore durante il salvataggio');
            }
        } catch (err) {
            console.error('Errore salvataggio evento:', err);
            setError('Errore di connessione');
        } finally {
            setUploading(false);
        }
    };

    // Elimina un evento
    const handleDelete = async (eventId: string) => {
        if (!confirm('Sei sicuro di voler eliminare questo evento?')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/tenant/self/documents/${eventId}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                await loadEvents();
            } else {
                setError('Errore durante l\'eliminazione');
            }
        } catch (err) {
            console.error('Errore eliminazione evento:', err);
            setError('Errore di connessione');
        }
    };

    // Prepara la modifica di un evento
    const handleEdit = (event: EventDocument) => {
        setEditingEvent(event);
        setFormData({
            name: event.name,
            description: event.description || '',
            file: null
        });
        setShowForm(true);
        setError(null);
    };

    // Gestisce la selezione del file
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                setError('Seleziona un file PDF valido');
                return;
            }
            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                setError('Il file deve essere inferiore a 10MB');
                return;
            }
            setFormData(prev => ({ ...prev, file }));
            setError(null);
        }
    };

    return (
        <div className="events-management">
            <div className="page-header">
                <div className="header-content">
                    <span className="header-icon">📅</span>
                    <h1>Gestione Eventi e Comunicazioni</h1>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => setShowForm(true)}
                >
                    + Nuovo Evento
                </button>
            </div>

            {error && (
                <div className="alert alert-error">
                    ⚠️ {error}
                </div>
            )}

            {/* Form di creazione/modifica */}
            {showForm && (
                <div className="form-overlay">
                    <div className="form-modal">
                        <div className="form-header">
                            <h2>{editingEvent ? 'Modifica Evento' : 'Nuovo Evento'}</h2>
                            <button className="close-btn" onClick={resetForm}>×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="event-form">
                            <div className="form-group">
                                <label htmlFor="name">Nome Evento *</label>
                                <input
                                    type="text"
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="es. Festa della Birra, Karaoke Live, Promozione Speciale"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Descrizione</label>
                                <textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Descrizione dettagliata dell'evento o comunicazione"
                                    rows={3}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="file">
                                    File PDF {editingEvent ? '(Opzionale - lascia vuoto per mantenere il file esistente)' : '*'}
                                </label>
                                <div className="file-input-wrapper">
                                    <input
                                        type="file"
                                        id="file"
                                        accept=".pdf"
                                        onChange={handleFileChange}
                                        required={!editingEvent}
                                    />
                                    {formData.file && (
                                        <div className="file-selected">
                                            📄 {formData.file.name}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={resetForm}
                                >
                                    Annulla
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={uploading}
                                >
                                    {uploading ? 'Salvando...' : (editingEvent ? 'Aggiorna' : 'Crea Evento')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Lista eventi */}
            <div className="events-list">
                {loading ? (
                    <div className="loading-state">
                        <p>Caricamento eventi...</p>
                    </div>
                ) : events.length === 0 ? (
                    <div className="empty-state">
                        <span style={{ fontSize: '48px' }}>📅</span>
                        <h3>Nessun evento creato</h3>
                        <p>Inizia creando il tuo primo evento o comunicazione</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowForm(true)}
                        >
                            + Crea Primo Evento
                        </button>
                    </div>
                ) : (
                    <div className="events-grid">
                        {events.map((event) => (
                            <div key={event.id} className="event-card">
                                <div className="event-header">
                                    <div className="event-info">
                                        <h3>{event.name}</h3>
                                        {event.description && (
                                            <p className="event-description">{event.description}</p>
                                        )}
                                    </div>
                                    <div className="event-actions">
                                        {event.url && (
                                            <button
                                                className="btn-icon"
                                                onClick={() => window.open(event.url, '_blank')}
                                                title="Visualizza PDF"
                                            >
                                                👁️
                                            </button>
                                        )}
                                        <button
                                            className="btn-icon"
                                            onClick={() => handleEdit(event)}
                                            title="Modifica"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className="btn-icon btn-danger"
                                            onClick={() => handleDelete(event.id)}
                                            title="Elimina"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>

                                <div className="event-meta">
                                    <span className="event-date">
                                        Creato: {new Date(event.createdAt).toLocaleDateString('it-IT')}
                                    </span>
                                    {event.updatedAt !== event.createdAt && (
                                        <span className="event-date">
                                            Aggiornato: {new Date(event.updatedAt).toLocaleDateString('it-IT')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventsManagement;